import { useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// Default logo - use the transparent simple version
const DEFAULT_LOGO = '/api/uploads/logo_simple_transparent.png';

// Cache for settings to avoid repeated API calls
let cachedSettings = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 60000; // 1 minute cache

const fetchSettings = async () => {
  const now = Date.now();
  if (cachedSettings && (now - cacheTimestamp) < CACHE_DURATION) {
    return cachedSettings;
  }
  
  try {
    const res = await fetch(`${API}/api/settings/public`);
    if (res.ok) {
      cachedSettings = await res.json();
      cacheTimestamp = now;
      return cachedSettings;
    }
  } catch (e) {
    console.log('Using default logo settings');
  }
  return null;
};

// Pre-fetch settings on module load
fetchSettings();

export const MadBrothersLogo = ({ 
  className = "", 
  size = "md", 
  variant = "dark", 
  showText = true,
  forceSettings = null 
}) => {
  const [settings, setSettings] = useState(forceSettings || cachedSettings);
  const [isLoading, setIsLoading] = useState(!cachedSettings && !forceSettings);
  
  useEffect(() => {
    if (!forceSettings && !cachedSettings) {
      fetchSettings().then(s => {
        if (s) setSettings(s);
        setIsLoading(false);
      });
    }
  }, [forceSettings]);

  const sizes = {
    sm: { logo: "h-10 w-10", title: "text-lg", subtitle: "text-[13px]", gap: "gap-2" },
    md: { logo: "h-14 w-14", title: "text-xl", subtitle: "text-[14px]", gap: "gap-2" },
    lg: { logo: "h-18 w-18", title: "text-2xl", subtitle: "text-[15px]", gap: "gap-3" },
    xl: { logo: "h-20 w-20", title: "text-[26px]", subtitle: "text-[17px]", gap: "gap-3" },
    xxl: { logo: "h-24 w-24", title: "text-3xl", subtitle: "text-[18px]", gap: "gap-4" }
  };

  const colors = {
    dark: { 
      title: "#1a1a1a", 
      subtitle: "#8D6E63",
      accent: "#C4A484"
    },
    light: { 
      title: "#ffffff", 
      subtitle: "#D4AF37",
      accent: "#E8D5B7"
    }
  };

  // Use settings if available, otherwise use props
  const logoSize = settings?.logo_size || size;
  const displayText = settings?.show_logo_text !== undefined ? settings.show_logo_text : showText;
  const logoShape = settings?.logo_shape || 'circle';
  const logoUrl = settings?.logo_image || DEFAULT_LOGO;
  
  const s = sizes[logoSize] || sizes.md;
  const c = colors[variant];

  // Shape classes - no shadow for better blending
  const shapeClasses = {
    circle: 'rounded-full',
    square: 'rounded-none',
    rounded: 'rounded-xl'
  };

  const fullLogoUrl = logoUrl.startsWith('http') ? logoUrl : `${API}${logoUrl}`;

  // Don't render anything while loading to prevent flash
  if (isLoading) {
    return (
      <div className={`flex items-center ${s.gap} ${className}`}>
        <div className={`${s.logo} ${shapeClasses[logoShape]} bg-stone-100 animate-pulse`} />
        {showText && (
          <div className="flex flex-col gap-1">
            <div className="h-7 w-28 bg-stone-100 rounded animate-pulse" />
            <div className="h-3 w-20 bg-stone-100 rounded animate-pulse" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center ${s.gap} ${className}`}>
      {/* Logo Image */}
      <img 
        src={fullLogoUrl}
        alt="Mad Brothers Bakery"
        className={`${s.logo} object-contain ${shapeClasses[logoShape]}`}
        style={{
          background: 'transparent',
          filter: variant === 'light' ? 'invert(1) brightness(1.15)' : 'none',
        }}
        data-testid="mad-brothers-logo"
        onError={(e) => {
          e.target.src = `${API}${DEFAULT_LOGO}`;
        }}
      />
      
      {/* Brand Text - Refined Typography */}
      {displayText && (
        <div className="flex flex-col leading-none">
          {/* Main Title - Elegant Playfair - Stacked */}
          <span 
            className={`font-['Playfair_Display'] ${s.title} font-bold tracking-tight leading-[0.9]`}
            style={{ color: c.title }}
          >
            Mad
          </span>
          <span 
            className={`font-['Playfair_Display'] ${s.title} font-bold tracking-tight leading-[1]`}
            style={{ color: c.title }}
          >
            Brothers
          </span>
          {/* Subtitle - Elegant Script Style */}
          <span 
            className={`font-['Great_Vibes',_cursive] ${s.subtitle} mt-1`}
            style={{ 
              color: c.subtitle,
              letterSpacing: '0.05em'
            }}
          >
            ✦ Artisan Bakery ✦
          </span>
        </div>
      )}
    </div>
  );
};

// Simple logo only (no text) for smaller spaces
export const MadBrothersLogoIcon = ({ size = "md", className = "", forceSettings = null }) => {
  const [settings, setSettings] = useState(forceSettings || cachedSettings);
  const [isLoading, setIsLoading] = useState(!cachedSettings && !forceSettings);
  
  useEffect(() => {
    if (!forceSettings && !cachedSettings) {
      fetchSettings().then(s => {
        if (s) setSettings(s);
        setIsLoading(false);
      });
    }
  }, [forceSettings]);

  const sizes = {
    sm: "h-10 w-10",
    md: "h-14 w-14",
    lg: "h-16 w-16",
    xl: "h-20 w-20"
  };

  const logoShape = settings?.logo_shape || 'circle';
  const logoUrl = settings?.logo_image || DEFAULT_LOGO;
  
  const shapeClasses = {
    circle: 'rounded-full',
    square: 'rounded-none',
    rounded: 'rounded-xl'
  };

  const fullLogoUrl = logoUrl.startsWith('http') ? logoUrl : `${API}${logoUrl}`;

  if (isLoading) {
    return <div className={`${sizes[size]} ${shapeClasses[logoShape]} bg-stone-100 animate-pulse ${className}`} />;
  }

  return (
    <img 
      src={fullLogoUrl}
      alt="Mad Brothers Bakery"
      className={`${sizes[size]} object-contain ${shapeClasses[logoShape]} ${className}`}
      style={{ background: 'transparent' }}
      data-testid="mad-brothers-logo-icon"
      onError={(e) => {
        e.target.src = `${API}${DEFAULT_LOGO}`;
      }}
    />
  );
};

export default MadBrothersLogo;
